/**
 * «Что общего у ваших отметок» — разбор свечей, которые человек отметил.
 *
 * Перебор готовых блоков отвечает на вопрос «какое из 42 правил ближе»,
 * а человек спрашивает другое: «что было в этих местах». Здесь у каждой
 * отмеченной свечи меряются простые вещи — RSI, где цена в диапазоне,
 * далеко ли от средних, объём, сила тренда, цвет свечи — и сравниваются
 * с обычными свечами той же истории. Если отметки сбились в край, где
 * обычная свеча бывает редко, это и есть общее: «у 6 из 7 отметок RSI
 * ниже 32, а так бывает лишь в 14 % свечей». Порог берётся из самих
 * отметок, поэтому и условие из него ловит именно их.
 *
 * Второе — сторона. У каждого условия есть наклон: «цена выше средней» —
 * к росту, «RSI выше 70» — к падению, «объём выше обычного» — ни к чему.
 * Для продажи бычьи условия не предлагаются, а у односторонних блоков
 * («MACD бычий», «цена выше VWAP») появляется зеркальное отражение.
 */
import { isEventCondition, SIGNAL_BLOCKS } from './blocks.js';
import { RULE_INDICATORS } from './indicators.js';
/* -------------------------------------------------------------- наклон */
const OVERLAYS = new Set(['ema', 'sma', 'wma', 'vwap', 'supertrend', 'bollinger', 'swing', 'ichimoku', 'psar', 'keltner', 'donchian', 'pivots']);
/** Осцилляторы: шкала, зона «дёшево» и «дорого». */
const OSCILLATORS = {
    rsi: { low: 35, high: 65, mid: 50, bounded: true },
    stochastic: { low: 30, high: 70, mid: 50, bounded: true },
    premium: { low: 40, high: 60, mid: 50, bounded: true },
    mfi: { low: 30, high: 70, mid: 50, bounded: true },
    williams_r: { low: -70, high: -30, mid: -50, bounded: true },
    cci: { low: -80, high: 80, mid: 0, bounded: false },
};
/** Величины со знаком: выше нуля — к росту. */
const SIGNED = new Set(['roc', 'macd', 'spot_cvd', 'futures_cvd', 'aroon']);
const UP_OUTPUTS = new Set(['up', 'hidden_up', 'triple_up']);
const DOWN_OUTPUTS = new Set(['down', 'hidden_down', 'triple_down']);
function unwrap(operand) {
    return operand.kind === 'recent' || operand.kind === 'previous' || operand.kind === 'scaled' ? unwrap(operand.of) : operand;
}
function isFlagOperand(operand) {
    const base = unwrap(operand);
    if (base.kind !== 'indicator')
        return false;
    const spec = RULE_INDICATORS.find((entry) => entry.id === base.indicator);
    if (!spec)
        return false;
    return spec.flag === true || spec.outputs?.find((entry) => entry.key === base.output)?.flag === true;
}
/** Цена или то, что рисуется поверх цены: средние, полосы, экстремумы. */
function priceLike(operand) {
    const base = unwrap(operand);
    if (base.kind === 'price')
        return true;
    return base.kind === 'indicator' && OVERLAYS.has(base.indicator);
}
function sign(operator) {
    if (operator === 'above' || operator === 'cross_above')
        return 1;
    if (operator === 'below' || operator === 'cross_below')
        return -1;
    return 0;
}
function bySign(value) {
    return value > 0 ? 'up' : value < 0 ? 'down' : 'neutral';
}
/**
 * К росту, к падению или ни к чему.
 *
 * Правила — как читает трейдер: цена над линией — к росту; осциллятор в
 * нижней зоне — перепродан, к росту; пересечение уровня вверх — к росту;
 * объём, волатильность и сила тренда стороны не имеют.
 */
export function biasOf(condition) {
    const left = unwrap(condition.left);
    const right = condition.right;
    const op = condition.operator;
    if (left.kind === 'indicator' && left.indicator === 'script')
        return left.output === 'sell' ? 'down' : left.output === 'alert' || left.output?.startsWith('plot') ? 'neutral' : 'up';
    if (isFlagOperand(condition.left)) {
        const output = left.kind === 'indicator' ? left.output : undefined;
        if (left.kind === 'indicator' && left.indicator === 'sweep')
            return output === 'low' ? 'up' : output === 'high' ? 'down' : 'neutral';
        if (output && UP_OUTPUTS.has(output))
            return 'up';
        if (output && DOWN_OUTPUTS.has(output))
            return 'down';
        return 'neutral';
    }
    if (left.kind === 'indicator' && left.indicator === 'trend_structure' && right.kind === 'number') {
        const value = op === 'equal' ? right.value : sign(op) * (right.value === 0 ? 1 : right.value);
        return bySign(value);
    }
    // Цена против цены или линии на цене.
    if (priceLike(condition.left) && priceLike(right))
        return bySign(sign(op));
    if (left.kind === 'indicator') {
        const oscillator = OSCILLATORS[left.indicator];
        if (oscillator && right.kind === 'number') {
            if (op === 'cross_above' || op === 'cross_below')
                return bySign(sign(op));
            if (op === 'below' && right.value <= oscillator.low)
                return 'up';
            if (op === 'above' && right.value >= oscillator.high)
                return 'down';
            return bySign(sign(op));
        }
        if (SIGNED.has(left.indicator) && right.kind === 'number' && right.value === 0)
            return bySign(sign(op));
        // Линия против линии той же природы: быстрая над медленной, MACD над сигнальной, OBV выше, чем был.
        const other = unwrap(right);
        if (other.kind === 'indicator' && other.indicator === left.indicator && !['atr', 'stddev', 'volume_sma', 'relative_volume', 'adx'].includes(left.indicator))
            return bySign(sign(op));
        if (oscillator && other.kind === 'indicator')
            return bySign(sign(op));
    }
    return 'neutral';
}
/** Годится ли условие для этой стороны: противоположный наклон — нет. */
export function fitsSide(condition, direction) {
    const bias = biasOf(condition);
    if (direction === 'LONG')
        return bias !== 'down';
    if (direction === 'SHORT')
        return bias !== 'up';
    return true;
}
/* -------------------------------------------------------------- зеркало */
const SWAP_OUTPUT = {
    up: 'down',
    down: 'up',
    high: 'low',
    low: 'high',
    hidden_up: 'hidden_down',
    hidden_down: 'hidden_up',
    triple_up: 'triple_down',
    triple_down: 'triple_up',
    upper: 'lower',
    lower: 'upper',
    cloud_top: 'cloud_bottom',
    cloud_bottom: 'cloud_top',
    r1: 's1',
    s1: 'r1',
    r2: 's2',
    s2: 'r2',
};
const SWAP_OPERATOR = {
    above: 'below',
    below: 'above',
    cross_above: 'cross_below',
    cross_below: 'cross_above',
};
function mirrorOperand(operand, scale) {
    switch (operand.kind) {
        case 'number':
            if (!scale)
                return operand;
            return { kind: 'number', value: scale.bounded ? 2 * scale.mid - operand.value : -operand.value };
        case 'indicator': {
            const swapped = operand.output ? SWAP_OUTPUT[operand.output] : undefined;
            return swapped ? { ...operand, output: swapped } : operand;
        }
        case 'price':
            return operand.source === 'high' ? { ...operand, source: 'low' } : operand.source === 'low' ? { ...operand, source: 'high' } : operand;
        case 'recent': {
            const of = mirrorOperand(operand.of, scale);
            const { lowest: _lowest, ...rest } = operand;
            return operand.lowest ? { ...rest, of } : { ...rest, of, lowest: true };
        }
        case 'previous':
            return { ...operand, of: mirrorOperand(operand.of, scale) };
        case 'scaled':
            return { ...operand, of: mirrorOperand(operand.of, scale), factor: Math.round((2 - operand.factor) * 1000) / 1000 };
        default:
            return operand;
    }
}
/**
 * Отражение условия на другую сторону: «цена выше VWAP» → «цена ниже VWAP»,
 * «RSI ниже 30» → «RSI выше 70», «снятие минимумов» → «снятие максимумов».
 * У нейтральных условий отражения нет — они и так годятся обеим сторонам.
 */
export function mirrorCondition(condition) {
    const bias = biasOf(condition);
    if (bias === 'neutral')
        return null;
    const left = unwrap(condition.left);
    const scale = left.kind === 'indicator' ? OSCILLATORS[left.indicator] ?? (SIGNED.has(left.indicator) ? { mid: 0, bounded: false } : undefined) : undefined;
    if (isFlagOperand(condition.left))
        return { ...condition, left: mirrorOperand(condition.left) };
    if (left.kind === 'indicator' && left.indicator === 'trend_structure' && condition.operator === 'equal' && condition.right.kind === 'number') {
        return { ...condition, right: { kind: 'number', value: -condition.right.value } };
    }
    const operator = SWAP_OPERATOR[condition.operator];
    if (!operator)
        return null;
    // Числа отражаются только у осцилляторов: у цены «выше 100» зеркала нет.
    const right = condition.right.kind === 'number' ? mirrorOperand(condition.right, scale) : mirrorOperand(condition.right);
    return { ...condition, operator, right };
}
const ind = (indicator, params, output) => ({ kind: 'indicator', indicator, params, ...(output ? { output } : {}) });
const close = { kind: 'price', source: 'close' };
function oscillator(id, title, operand, low, high) {
    return {
        id,
        title,
        unit: '',
        step: 1,
        values: (bars, resolve) => resolve(operand.indicator, operand.params, operand.output, bars),
        state: (way, threshold) => ({ left: operand, operator: way, right: { kind: 'number', value: threshold } }),
        events: (_way, threshold) => [
            { left: operand, operator: 'cross_above', right: { kind: 'number', value: threshold } },
            { left: operand, operator: 'cross_below', right: { kind: 'number', value: threshold } },
        ],
        words: (way) => (way === 'below' ? low : high),
    };
}
function distance(id, period) {
    const line = ind('ema', { period });
    return {
        id,
        title: `Расстояние до EMA ${period}`,
        unit: '%',
        step: 0.1,
        values: (bars, resolve) => {
            const ema = resolve('ema', { period }, undefined, bars);
            return bars.map((bar, i) => {
                const value = ema[i];
                return value == null || value === 0 ? null : (bar.close / value - 1) * 100;
            });
        },
        state: (way, threshold) => ({ left: close, operator: way, right: threshold === 0 ? line : { kind: 'scaled', of: line, factor: Math.round((1 + threshold / 100) * 1000) / 1000 } }),
        words: (way) => (way === 'below' ? `цена под EMA ${period}` : `цена над EMA ${period}`),
    };
}
const FEATURES = [
    oscillator('rsi', 'RSI (14)', ind('rsi', { period: 14 }), 'RSI низкий', 'RSI высокий'),
    oscillator('range', 'Где цена в диапазоне 20 свечей', ind('stochastic', { period: 20, smooth: 3 }, 'k'), 'цена у дна диапазона', 'цена у вершины диапазона'),
    oscillator('cci', 'CCI (20)', ind('cci', { period: 20 }), 'CCI глубоко внизу', 'CCI высоко'),
    distance('ema50', 50),
    distance('ema200', 200),
    {
        id: 'move',
        title: 'Ход цены за 9 свечей',
        unit: '%',
        step: 0.1,
        values: (bars, resolve) => resolve('roc', { period: 9 }, undefined, bars),
        state: (way, threshold) => ({ left: ind('roc', { period: 9 }), operator: way, right: { kind: 'number', value: threshold } }),
        words: (way) => (way === 'below' ? 'перед этим цена падала' : 'перед этим цена росла'),
    },
    {
        id: 'volume',
        title: 'Объём к обычному',
        unit: '×',
        step: 0.1,
        values: (bars, resolve) => resolve('relative_volume', { period: 20 }, undefined, bars),
        state: (way, threshold) => ({ left: ind('relative_volume', { period: 20 }), operator: way, right: { kind: 'number', value: threshold } }),
        events: (way, threshold) => (way === 'above' ? [{ left: ind('relative_volume', { period: 20 }), operator: 'cross_above', right: { kind: 'number', value: threshold } }] : []),
        words: (way) => (way === 'below' ? 'объём ниже обычного' : 'объём выше обычного'),
    },
    {
        id: 'adx',
        title: 'Сила тренда (ADX)',
        unit: '',
        step: 1,
        values: (bars, resolve) => resolve('adx', { period: 14 }, undefined, bars),
        state: (way, threshold) => ({ left: ind('adx', { period: 14 }), operator: way, right: { kind: 'number', value: threshold } }),
        words: (way) => (way === 'below' ? 'тренд слабый' : 'тренд сильный'),
    },
    {
        id: 'candle',
        title: 'Свеча',
        unit: '',
        step: 1,
        // Не число, а цвет: 1 — выросла, −1 — упала. Край «выше» — зелёные, «ниже» — красные.
        values: (bars) => bars.map((bar) => (bar.close > bar.open ? 1 : bar.close < bar.open ? -1 : 0)),
        state: (way) => ({ left: close, operator: way, right: { kind: 'price', source: 'open' } }),
        words: (way) => (way === 'below' ? 'свеча закрылась падением' : 'свеча закрылась ростом'),
    },
];
function round(value, step) {
    const rounded = Math.round(value / step) * step;
    return Math.round(rounded * 1000) / 1000;
}
function quantile(sorted, q) {
    if (sorted.length === 0)
        return 0;
    const at = Math.min(sorted.length - 1, Math.max(0, Math.round(q * (sorted.length - 1))));
    return sorted[at];
}
/**
 * Разобрать отметки: у каких признаков отметки лежат в краю, где обычная
 * свеча бывает редко. Порог — такой, чтобы за него попали 80 % отметок.
 */
export function profileMarks(bars, want, resolve) {
    if (want.length === 0)
        return [];
    const traits = [];
    for (const feature of FEATURES) {
        let series;
        try {
            series = feature.values(bars, resolve);
        }
        catch {
            continue;
        }
        const all = series.filter((value) => value !== null && Number.isFinite(value)).sort((a, b) => a - b);
        const at = want.map((index) => series[index]).filter((value) => value != null && Number.isFinite(value));
        if (all.length < 50 || at.length < Math.min(3, want.length))
            continue;
        const share = (test) => all.filter(test).length / all.length;
        const sortedMarks = [...at].sort((a, b) => a - b);
        const cover = Math.max(1, Math.ceil(at.length * 0.8));
        for (const way of ['below', 'above']) {
            let threshold;
            if (feature.id === 'candle') {
                threshold = 0;
            }
            else {
                const raw = way === 'below' ? sortedMarks[cover - 1] : sortedMarks[sortedMarks.length - cover];
                // Округление наружу: порог не должен отрезать отметку, ради которой взят.
                const step = feature.step;
                threshold = way === 'below' ? round(Math.ceil(raw / step) * step, step) : round(Math.floor(raw / step) * step, step);
            }
            const inside = (value) => (way === 'below' ? value < threshold || (feature.id !== 'candle' && value === threshold) : value > threshold || (feature.id !== 'candle' && value === threshold));
            const hit = at.filter(inside).length;
            const usual = share(inside);
            // Примечательно, если отметки почти все там, а обычная свеча — редко.
            const strong = hit / at.length >= 0.85 && usual <= 0.6;
            if (!strong && (hit / at.length < 0.7 || usual > 0.45))
                continue;
            traits.push({
                id: `${feature.id}-${way}`,
                title: feature.title,
                words: feature.words(way),
                way,
                threshold,
                unit: feature.unit,
                hit,
                of: at.length,
                usual,
                spread: [quantile(all, 0.1), quantile(all, 0.9)],
                marks: at,
                condition: feature.state ? feature.state(way, threshold) : null,
                events: feature.events ? feature.events(way, threshold) : [],
            });
        }
    }
    // Сначала самое примечательное: много отметок там, где свечей мало.
    return traits.sort((a, b) => b.hit / b.of / Math.max(0.02, b.usual) - a.hit / a.of / Math.max(0.02, a.usual));
}
/* -------------------------------------------------------------- кандидаты */
/*
 * Разворот и продолжение — события, которые случаются в пределах свечи-двух
 * от точки, куда человек ставит палец. Готовые блоки ловят уровень («RSI
 * пересёк 30»), а донышко бывает и на 24, и на 41; эти — ловят сам поворот.
 * Здесь — сторона покупки, продажа получает их зеркала.
 */
const rsi14 = ind('rsi', { period: 14 });
const TURNS = [
    ['turn-rsi', { left: rsi14, operator: 'cross_above', right: { kind: 'previous', of: rsi14, back: 1 } }],
    ['turn-high', { left: close, operator: 'cross_above', right: { kind: 'previous', of: { kind: 'price', source: 'high' }, back: 1 } }],
    ['turn-ema9', { left: close, operator: 'cross_above', right: ind('ema', { period: 9 }) }],
    ['turn-stoch', { left: ind('stochastic', { period: 14, smooth: 3 }, 'k'), operator: 'cross_above', right: ind('stochastic', { period: 14, smooth: 3 }, 'd') }],
];
function asBlock(id, condition, hint, category = 'price') {
    return { id, label: id, hint, category, nature: isEventCondition(condition) ? 'event' : 'state', make: () => condition };
}
/**
 * Из чего собирать правило для этой стороны: готовые блоки с подходящим
 * наклоном, их зеркала для другой стороны и условия с порогами из отметок.
 *
 * Фон из отметок сторону не проверяет: он описывает сами отметки («у
 * донышек цена под EMA 50» — факт, а не медвежий довод). Главное событие
 * проверяет всегда: покупка на пересечении вниз читается как ошибка.
 */
export function sketchCandidates(direction, traits, blocks = SIGNAL_BLOCKS) {
    const seen = new Set();
    const out = [];
    const add = (block) => {
        const key = JSON.stringify(block.make());
        if (seen.has(key))
            return;
        seen.add(key);
        out.push(block);
    };
    for (const trait of traits) {
        if (trait.condition)
            add(asBlock(`trait-${trait.id}`, trait.condition, `Общее у отметок: ${trait.words}`));
        trait.events.forEach((event, n) => {
            if (fitsSide(event, direction) || biasOf(event) === 'neutral')
                add(asBlock(`trait-${trait.id}-e${n}`, event, `Общее у отметок: ${trait.words}`));
        });
    }
    for (const [id, condition] of TURNS) {
        const turned = direction === 'SHORT' ? mirrorCondition(condition) : condition;
        if (turned)
            add(asBlock(`${id}${direction === 'SHORT' ? '~mirror' : ''}`, turned, 'Разворот рядом с отметкой', 'momentum'));
    }
    for (const block of blocks) {
        const condition = block.make();
        if (fitsSide(condition, direction))
            add(block);
        const mirrored = mirrorCondition(condition);
        if (mirrored && fitsSide(mirrored, direction))
            add({ ...block, id: `${block.id}~mirror`, label: block.label, make: () => mirrored });
    }
    return out;
}
//# sourceMappingURL=sketch-profile.js.map